// R77 — Realtime hook voor Sikkens Center export-log.
// Levert Map<orderid, exported_at> en update live bij INSERT.
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

const QUERY_KEY = ["sikkens-export-log"] as const;

export type SikkensExportMap = Map<number, string>;

interface Row { orderid: number; exported_at: string }

export const useSikkensExportLog = () => {
  const { isAuthenticated, isSessionValid, isValidating } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY,
    enabled: isAuthenticated && isSessionValid && !isValidating,
    staleTime: 60_000,
    queryFn: async (): Promise<SikkensExportMap> => {
      // Houd alleen de meest recente export per orderid.
      const { data, error } = await supabase
        .from("decoprof_sikkens_export_log")
        .select("orderid, exported_at")
        .order("exported_at", { ascending: false })
        .limit(5000);
      if (error) throw error;
      const map: SikkensExportMap = new Map();
      for (const r of (data ?? []) as Row[]) {
        if (!map.has(r.orderid)) map.set(r.orderid, r.exported_at);
      }
      return map;
    },
  });

  useEffect(() => {
    if (!isAuthenticated || !isSessionValid || isValidating) return;
    const channel = supabase
      .channel("sikkens-export-log-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "decoprof_sikkens_export_log" },
        (payload) => {
          const row = payload.new as Row | null;
          if (!row?.orderid) return;
          qc.setQueryData<SikkensExportMap>(QUERY_KEY, (prev) => {
            const next = new Map(prev ?? []);
            const existing = next.get(row.orderid);
            if (!existing || existing < row.exported_at) next.set(row.orderid, row.exported_at);
            return next;
          });
        },
      )
      .subscribe();
    return () => { try { supabase.removeChannel(channel); } catch { /* noop */ } };
  }, [isAuthenticated, isSessionValid, isValidating, qc]);

  return query;
};

export const formatExportedAt = (iso: string): string => {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  }).format(new Date(t)).replace(" om ", ", ");
};
